// @flow

import crypto from 'crypto';
import {Tag, ContentGroupItem} from 'docs/util';

import type {MarkdocTagRegistration} from 'docs/markdoc';
import type {Node as MarkdocAstNode} from '@markdoc/markdoc';

const generateCodeTabID = (node: MarkdocAstNode) => {
  // NOTE (matv): It would be beneficial if every Markdoc node had a
  // stable ID generated that did not depend directly on the content itself.
  // This could potentially beneficial for editing purposes, translation,
  // and more. This code tab ID is used for code copying analytics and
  // rendering a copy button.

  // We do this on a AST node instead of a render tag or in React,
  // because we should be able to compute IDs without needing to render the
  // content.
  const rawContent = node.children
    .map((child) =>
      child.children ? child.children[0]?.attributes?.content : '',
    )
    .join('');

  // This hash function is the fastest one in Node. It takes < 5 ms.
  const hash = crypto
    .createHash('sha1')
    .update(node.lines[0] + rawContent)
    .digest('hex');

  return `codetabs_${hash}`;
};

const PREF_TO_VALID_LANGS = {
  'html-or-react': ['html', 'react'],
  'lang-android': ['kotlin', 'java'],
  'lang-ios': ['swift', 'objc'],
  'lang-js': ['js', 'esnext'],
  lang: [
    'curl',
    'cli',
    'ruby',
    'python',
    'php',
    'java',
    'node',
    'go',
    'dotnet',
  ],
  'html-or-css': ['html', 'css'],
  'react-hooks': ['hooks', 'class-components'],
};

export const CodeTabGroup: MarkdocTagRegistration = {
  tag: 'code-tabs',
  component: 'CodeTabGroupMarkdocShim',
  attributes: {
    pref: {
      type: String,
      required: false,
      errorLevel: 'critical',
      default: 'lang',
      matches: [
        'html-or-react',
        'lang-android',
        'lang-ios',
        'lang-js',
        'lang',
        'html-or-css',
        'react-hooks',
      ],
      description:
        "Set this store the user's selection in a pref (a cookie). This allows syncing across code groups.",
    },
    type: {
      type: String,
      required: false, // TODO (matv): change to required: true
      errorLevel: 'critical',
      matches: [
        'other',
        'android',
        'ios',
        'js',
        'server',
        'other',
        'react-style',
      ],
      description:
        'Specify what the type of content inside of these code tabs are',
    },
  },
  validate(node) {
    const langs = node.children
      .filter((child) => {
        return child.type === 'tag:code-tab';
      })
      .map((child) => child.attributes.id);

    const pref = node.attributes.pref;

    if (node.attributes.type === 'other') {
      return [];
    }

    if (!pref) {
      return [
        {
          id: 'code-snippet-invalid-pref-for-type',
          level: 'critical',
          message: `You have a code tab with the following languages: ${langs.join(
            ', ',
          )}, but did not specify a pref. If this is a special case, set type="other". Read the docs for more.`,
        },
      ];
    }

    const validLangs = PREF_TO_VALID_LANGS[pref];

    // Right now we are allowing the langs for a given pref to be a subset of the valid langs.
    // Should we enforce that to be an exact match? For example, you can have pref="lang", but
    // only include "java", which feels wrong.
    const invalidLangs = langs.filter((lang) => !validLangs.includes(lang));

    if (invalidLangs.length > 0) {
      return [
        {
          id: 'code-snippet-invalid-langs',
          level: 'critical',
          message: `You specified a pref of ${pref} which means the valid langs are ${validLangs.join(
            ', ',
          )}. You used the following invalid langs: ${invalidLangs.join(', ')}`,
        },
      ];
    }

    return [];
  },
  render(node, config) {
    const codeTabGroupID = generateCodeTabID(node);

    const attributes = node.renderAttributes(this.attributes, config);
    const children = node.renderChildren(config);

    const items = ContentGroupItem.fromMarkdocTags(children);
    return new Tag(this.tag, {...attributes, items, codeTabGroupID});
  },
};
